import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, Briefcase, MessageCircle, User, Wrench } from 'lucide-react';
import { Badge } from './ui/badge';
import { useUnreadMessages } from '../lib/useUnreadMessages';
import { usePricing } from '../hooks/usePricing';

const BottomNav = ({ profile }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { unreadCount: unreadMessages } = useUnreadMessages(profile?.id);
  const { pricing } = usePricing();

  // Determine nav items based on role
  const getNavItems = () => {
    let items;
    if (profile?.role === 'job_seeker' || profile?.role === 'contractor') {
      items = [
        { label: t('nav.jobs'), icon: Briefcase, path: '/jobs', testId: 'nav-browse' },
        { label: t('nav.applied'), icon: LayoutDashboard, path: '/dashboard', testId: 'nav-applied' },
        { label: t('nav.messages'), icon: MessageCircle, path: '/messages', testId: 'nav-messages' },
        { label: t('nav.profile'), icon: User, path: '/profile', testId: 'nav-profile' }
      ];
    } else {
      // Default for employers and owners
      items = [
        { label: t('nav.dashboard'), icon: LayoutDashboard, path: '/dashboard', testId: 'nav-dashboard' },
        { label: t('nav.jobs'), icon: Briefcase, path: '/jobs', testId: 'nav-jobs' },
        { label: t('nav.messages'), icon: MessageCircle, path: '/messages', testId: 'nav-messages' },
        { label: t('nav.profile'), icon: User, path: '/profile', testId: 'nav-profile' }
      ];
    }
    // Services tab (flag-gated) — inserted right after Jobs
    if (pricing?.services_enabled) {
      const jobsIdx = items.findIndex((i) => i.path === '/jobs');
      const servicesItem = { label: t('nav.services'), icon: Wrench, path: '/services', testId: 'nav-services' };
      if (jobsIdx >= 0) items.splice(jobsIdx + 1, 0, servicesItem);
      else items.push(servicesItem);
    }
    return items;
  };

  const navItems = getNavItems();

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur border-t border-border md:hidden pb-safe"
      role="navigation"
      aria-label={t('nav.mainNavigation', 'Main navigation')}
    >
      <div className={`grid h-14`} style={{ gridTemplateColumns: `repeat(${navItems.length}, 1fr)` }}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path ||
                           (item.path === '/dashboard' && location.pathname.startsWith('/dashboard'));
          const hasUnread = item.path === '/messages' && unreadMessages > 0;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={item.testId}
              aria-label={hasUnread ? `${item.label} (${unreadMessages} unread)` : item.label}
              aria-current={isActive ? 'page' : undefined}
              className={`flex flex-col items-center justify-center gap-1 transition-colors relative touch-target tap-highlight ${
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="relative">
                <Icon size={20} aria-hidden="true" />
                {hasUnread && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-2 -right-3 px-1.5 py-0 text-xs h-5 min-w-[20px] flex items-center justify-center"
                    data-testid="messages-badge-mobile"
                    aria-hidden="true"
                  >
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </Badge>
                )}
              </div>
              <span className={`truncate max-w-full px-0.5 ${navItems.length >= 5 ? 'text-[11px]' : 'text-xs'}`}>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;